import { parseWithZod } from '@conform-to/zod';
import { invariantResponse } from '@epic-web/invariant';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction } from
'@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useLoaderData,
  Link,
  useOutlet,
  useLocation } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { AnimatePresence, motion } from 'framer-motion';
import { AlignJustify } from 'lucide-react';
import * as React from 'react';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { EpicProgress } from './components/progress-bar.tsx';
import { useToast } from './components/toaster.tsx';
import {
  Dialog,
  DialogContent,
  DialogTrigger } from
'./components/ui/dialog.tsx';
import { href as iconsHref } from './components/ui/icon.tsx';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle } from
'./components/ui/navigation-menu.tsx';
import { EpicToaster } from './components/ui/sonner.tsx';
import fontStyleSheetUrl from './styles/font.css';
import tailwindStyleSheetUrl from './styles/tailwind.css';
import { getUserId, logout } from './utils/auth.server.ts';
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { cn, combineHeaders, getDomainUrl } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { useRequestInfo } from './utils/request-info.ts';
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null, (
  // { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  // { rel: 'mask-icon', href: '/favicons/pom-pom.gif' },
  // {
  // 	rel: 'alternate icon',
  // 	type: 'image/png',
  // 	href: '/favicons/favicon-32x32.png',
  // },
  // { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  // { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.ico' },
  { rel: 'stylesheet', href: fontStyleSheetUrl },
  { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = () => {
  return [
  { title: 'Fiona | Koan' },
  { name: 'description', content: `Fiona and Koan's wedding website` }];

};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUniqueOrThrow({
      select: {
        id: true,
        name: true,
        username: true,
        image: { select: { id: true } },
        roles: {
          select: {
            name: true,
            permissions: {
              select: { entity: true, action: true, access: true }
            }
          }
        }
      },
      where: { id: userId }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request)
        }
      },
      ENV: getEnv(),
      toast,
      honeyProps
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark'])
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: ThemeFormSchema
  });

  invariantResponse(submission.status === 'success', 'Invalid theme received');

  const { theme } = submission.value;

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) }
  };
  return json({ result: submission.reply() }, responseInit);
}

function Document({
  children,
  nonce,
  theme = 'light',
  env = {}





}: {children: React.ReactNode;nonce: string;theme?: Theme;env?: Record<string, string>;}) {
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />

				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

const PAGES = [
{ label: 'HOME', href: '/' },
{ label: 'OUR STORY', href: '/our-story' },
{ label: 'GALLERY', href: '/gallery' },
{ label: 'DETAILS', href: '/details' },
{ label: 'BESTIES', href: '/besties' },
{ label: 'REGISTRY', href: '/registry' }];


function MobileMenu() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  return (
    <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
			<DialogTrigger className="lg:hidden">
				<AlignJustify size={28} />
			</DialogTrigger>
			<DialogContent className="fixed inset-0 flex w-full flex-col justify-between overflow-y-auto bg-white sm:ring-1 sm:ring-gray-900/10">
				<div className="p-6">
					<div className="mt-6 flow-root">
						<div className="-my-6 divide-y divide-gray-500/10">
							<div className="space-y-2 py-6">
								{PAGES.map((page) =>
                <Link
                  key={page.href}
                  to={page.href}
                  className={cn(
                    'group -mx-3 flex items-center gap-x-6 rounded-lg p-3 text-body-md font-semibold leading-7 text-gray-900 hover:bg-gray-50',
                    location.pathname === page.href && 'bg-gray-50'
                  )}
                  onClick={() => setIsMenuOpen(false)}>

										{page.label}
									</Link>
                )}
							</div>
							<div className="py-6">
								<Link
                  to="/rsvp"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-body-md font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}>

									RSVP
								</Link>
							</div>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>);

}

function NavigationHeader() {
  return (
    <React.Fragment>
			<div className="container absolute inset-x-0 top-6 z-20">
				<MobileMenu />
			</div>

			<NavigationMenu className="container hidden py-6 lg:block">
				<NavigationMenuList className="flex flex-wrap items-center gap-4 sm:flex-nowrap md:gap-8">
					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/" prefetch="render">
								HOME
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/our-story">OUR STORY</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/gallery">GALLERY</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/details">DETAILS</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/besties">BESTIES</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/registry">REGISTRY</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>

					<NavigationMenuItem>
						<NavigationMenuLink
              asChild
              className={navigationMenuTriggerStyle({
                className: 'md:text-2xl'
              })}>

							<Link to="/rsvp">RSVP</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		</React.Fragment>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const outlet = useOutlet();

  useToast(data.toast);

  return (
    <Document nonce={nonce} theme="light" env={data.ENV}>
			<div className="relative flex min-h-screen flex-col justify-between">
				<NavigationHeader />

				<AnimatePresence initial={false} mode="wait">
					<motion.div
            key={useLocation().pathname}
            className="flex flex-1 flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ y: '-5%', opacity: 0 }}
            transition={{ duration: 0.3 }}>

						{outlet}
					</motion.div>
				</AnimatePresence>
			</div>

			<EpicToaster closeButton position="top-center" theme="light" />
			<EpicProgress />
		</Document>);

}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>);

}

export default withSentry(AppWithProviders);

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode;
  }
  return requestInfo.userPrefs.theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) => f.formAction === '/');

  if (themeFetcher && themeFetcher.formData) {
    const submission = parseWithZod(themeFetcher.formData, {
      schema: ThemeFormSchema
    });

    if (submission.status === 'success') {
      return submission.value.theme;
    }
  }
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}